import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "@components/ui/Spinner";
import { useAdminClinics } from "@hooks/useAdminClinics";
import { impersonateUser } from "@services/auth";
import { downloadAndSaveClinicReport } from "@services/reports";

import AdminClinicsHeader from "@components/admin-clinics/AdminClinicsHeader";
import ClinicsTableDesktop from "@components/admin-clinics/ClinicsTableDesktop";
import ClinicsTableTablet from "@components/admin-clinics/ClinicsTableTablet";
import ClinicsMobileCards from "@components/admin-clinics/ClinicsMobileCards";
import ViewTextModal from "@components/admin-clinics/ViewTextModal";
import EditClinicDrawer from "@components/admin-clinics/EditClinicDrawer";
import ReportDateRangeModal from "@components/admin-clinics/ReportDateRangeModal";
import ToastHost from "@components/clinic/ToastHost";

export default function AdminClinics() {
  const navigate = useNavigate();

  const {
    loading,

    query,
    setQuery,
    filteredClinics,

    viewModal,
    openView,
    closeView,

    editModalOpen,
    openEdit,
    closeEdit,

    selectedClinicId,
    selectedClinic,
    draft,
    setDraftField,

    sikkaPractices,
    sikkaPracticesLoading,
    sikkaConnecting,
    loadSikkaPractices,
    connectSikka,

    purposes,
    questionLibrary,
    questionLibraryLoading,

    agentQuestionsByPurpose,
    setAgentQuestionsByPurpose,

    dirtyClinic,
    dirtyAgentQuestions,

    saving,
    save,

    isValidAgentQuestions,

    setupLoading,
    runSetup,
    toast,
    setToast,
  } = useAdminClinics();

  // Report modal state
  const [reportModal, setReportModal] = useState({
    open: false,
    clinic: null,
  });
  const [reportLoading, setReportLoading] = useState(false);

  const handleImpersonate = (clinic) => {
    impersonateUser(clinic.owner_user_id);
  };

  const openReportModal = (clinic) => {
    setReportModal({ open: true, clinic });
  };

  const closeReportModal = () => {
    setReportModal({ open: false, clinic: null });
  };

  const handleGenerateReport = async (startDate, endDate) => {
    if (!reportModal.clinic) return;

    setReportLoading(true);
    try {
      await downloadAndSaveClinicReport(
        reportModal.clinic.id,
        reportModal.clinic.clinic_name || "Clinic",
        startDate,
        endDate
      );
      setToast({ type: "success", message: "Report downloaded successfully" });
      closeReportModal();
    } catch (error) {
      setToast({ type: "error", message: error.message || "Failed to generate report" });
    } finally {
      setReportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 flex items-center gap-3">
        <Spinner />
        <span>Loading clinics…</span>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <AdminClinicsHeader
        onBack={() => navigate(-1)}
        query={query}
        onQueryChange={setQuery}
      />

      <div className="hidden lg:block">
        <ClinicsTableDesktop
          clinics={filteredClinics}
          onEdit={openEdit}
          onView={openView}
          onImpersonate={handleImpersonate}
          onRunSetup={runSetup}
          setupLoading={setupLoading}
          onReport={openReportModal}
        />
      </div>

      <div className="hidden md:block lg:hidden">
        <ClinicsTableTablet
          clinics={filteredClinics}
          onEdit={openEdit}
          onView={openView}
          onImpersonate={handleImpersonate}
          onRunSetup={runSetup}
          setupLoading={setupLoading}
          onReport={openReportModal}
        />
      </div>

      <div className="md:hidden">
        <ClinicsMobileCards
          clinics={filteredClinics}
          onEdit={openEdit}
          onView={openView}
          onImpersonate={handleImpersonate}
          onRunSetup={runSetup}
          setupLoading={setupLoading}
          onReport={openReportModal}
        />
      </div>

      <ViewTextModal
        open={viewModal.open}
        title={viewModal.title}
        text={viewModal.text}
        onClose={closeView}
      />

      <ToastHost toast={toast} onClose={() => setToast(null)} />

      <EditClinicDrawer
        open={editModalOpen}
        onClose={closeEdit}
        selectedClinicId={selectedClinicId}
        selectedClinic={selectedClinic}
        draft={draft}
        onChangeField={setDraftField}
        sikkaPractices={sikkaPractices}
        sikkaPracticesLoading={sikkaPracticesLoading}
        onLoadSikkaPractices={loadSikkaPractices}
        onConnectSikka={connectSikka}
        sikkaConnecting={sikkaConnecting}
        purposes={purposes}
        questionLibrary={questionLibrary}
        questionLibraryLoading={questionLibraryLoading}
        agentQuestionsByPurpose={agentQuestionsByPurpose}
        setAgentQuestionsByPurpose={setAgentQuestionsByPurpose}
        dirtyClinic={dirtyClinic}
        dirtyAgentQuestions={dirtyAgentQuestions}
        saving={saving}
        onSave={save}
        isValidAgentQuestions={isValidAgentQuestions}
      />

      <ReportDateRangeModal
        open={reportModal.open}
        clinicName={reportModal.clinic?.clinic_name}
        onClose={closeReportModal}
        onGenerate={handleGenerateReport}
        loading={reportLoading}
      />
    </div>
  );
}
